import { deviceVisibilityFromApi, deviceVisibilityToApiWithWp } from '@/api/adapters/deviceAccessAdapter'
import {
  geolocationsApiToModel,
  geolocationsModelToApi,
  leafletGeolocationTextareasToApi
} from '@/api/adapters/geolocationsAdapter'
import { longUrlApiToModel, normalizeLongUrls } from '@/api/adapters/longUrlAdapter'
import type {
  LeafletApiModel,
  LeafletDetailsApiModel,
  LeafletDetailsModel,
  LeafletFormPayload,
  LeafletModel
} from '@/api/leaflets'
import { extractCoordinates } from '@/views/dashboard/domain/geolocations'
import type { LeafletForm } from '@/views/dashboard/types/LeafletForm'

interface LeafletsMapperContract {
  mapLeafletFromApi(api: LeafletApiModel): LeafletModel
  mapLeafletDetailsFromApi(api: LeafletDetailsApiModel): LeafletDetailsModel
  mapLeafletListFromApi(apiList: LeafletApiModel[]): LeafletModel[]
  mapLeafletUpdateToApi(input: LeafletForm): LeafletFormPayload
}

const EMPTY_SCHEDULED_LONG_LINK = {
  date: null,
  time: null,
  longUrls: [],
  devices: []
}

const EMPTY_SCHEDULED_THUMBNAIL_LINK = {
  date: null,
  time: null,
  thumbnailURL: '',
  devices: []
}

function mapLeafletFromApi(api: LeafletApiModel): LeafletModel {
  const withGeolocations = geolocationsApiToModel(api, { includeAvailabilityType: true })
  const withLongUrls = longUrlApiToModel(withGeolocations)
  return deviceVisibilityFromApi(withLongUrls)
}

function mapLeafletDetailsFromApi(api: LeafletDetailsApiModel): LeafletDetailsModel {
  return deviceVisibilityFromApi(api)
}

function mapLeafletListFromApi(apiList: LeafletApiModel[]): LeafletModel[] {
  return apiList.map(mapLeafletFromApi)
}

function mapLeafletUpdateToApi(input: LeafletForm): LeafletFormPayload {
  const hasSeparateUnavailableGeolocations = Object.prototype.hasOwnProperty.call(input, 'unavailableGeolocations')
  const geolocations = hasSeparateUnavailableGeolocations
    ? leafletGeolocationTextareasToApi(input.geolocations, input.unavailableGeolocations ?? '')
    : extractCoordinates(input.geolocations, { includeAvailabilityType: true })
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { unavailableGeolocations: _, ...inputWithoutUnavailableGeolocations } = input
  const withLongUrls = {
    ...inputWithoutUnavailableGeolocations,
    geolocations,
    longUrls: normalizeLongUrls(input.longUrls),
    scheduledLongLink: {
      ...EMPTY_SCHEDULED_LONG_LINK,
      ...input.scheduledLongLink,
      longUrls: normalizeLongUrls(input.scheduledLongLink?.longUrls)
    },
    scheduledThumbnailTrackingLink: {
      ...EMPTY_SCHEDULED_THUMBNAIL_LINK,
      ...input.scheduledThumbnailTrackingLink
    }
  }
  const withDeviceVisibility = deviceVisibilityToApiWithWp(withLongUrls)
  return geolocationsModelToApi(withDeviceVisibility, { includeAvailabilityType: true })
}

export const leafletsMapper: LeafletsMapperContract = {
  mapLeafletFromApi,
  mapLeafletDetailsFromApi,
  mapLeafletListFromApi,
  mapLeafletUpdateToApi
}
